import { BillingEumEditType } from "~/graphql/supergraphEnums";
import { getReasonCodeLabel } from "../constants";
import {
  buildEumCalcEditChange,
  buildEumValueOverrideChange,
} from "./eumProposedChangesBuilders";
import type {
  ProposedChangeMetadata,
  ProposedChangesResult,
  StoredFamilyProposedChange,
  StoredProposedChange,
} from "../types";
import type { GetGpChangeRequestQuery, Maybe, Scalars } from "~/graphql/supergraphTypes";

const decimalStringToNumber = (
  value: Maybe<Scalars["Decimal"]["output"]> | undefined,
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const decimalStringToRequiredNumber = (value: Scalars["Decimal"]["output"]): number =>
  Number(value);

export const extractSupportingDocumentName = (
  s3DocumentKey: Maybe<string> | undefined,
): string | undefined => {
  if (!s3DocumentKey) {
    return undefined;
  }
  const segments = s3DocumentKey.split("/");
  const last = segments[segments.length - 1];
  return last && last.length > 0 ? last : undefined;
};

const buildItemMetadata = (
  reasonCode: string,
  justification: string,
  s3DocumentKey: Maybe<string> | undefined,
): ProposedChangeMetadata | undefined => {
  const supportingDocumentName = extractSupportingDocumentName(s3DocumentKey);
  if (!reasonCode && !justification && !supportingDocumentName) {
    return undefined;
  }
  return {
    reasonCode: reasonCode ? getReasonCodeLabel(reasonCode) : undefined,
    details: justification || undefined,
    supportingDocumentName,
    s3DocumentKey: s3DocumentKey ?? undefined,
  };
};

export const buildProposedChangesFromGpChangeRequest = (
  gpChangeRequest: GetGpChangeRequestQuery["getGpChangeRequest"],
): ProposedChangesResult => {
  const entityMap = new Map<number, StoredProposedChange[]>();
  const entityCounts = new Map<number, number>();
  const familyMap = new Map<string, StoredFamilyProposedChange>();
  const familyCounts = new Map<string, number>();
  const billingGroupChanges = new Map<string, StoredProposedChange[]>();

  for (const item of gpChangeRequest.items) {
    const entityName = item.entityName ?? "";
    const metadata = buildItemMetadata(
      item.reasonCode,
      item.justification,
      item.changeType === BillingEumEditType.EumValueOverride
        ? item.overridePayload?.s3DocumentKey
        : item.calcEditPayload?.s3DocumentKey,
    );

    let storedChange: StoredProposedChange | null = null;

    if (item.changeType === BillingEumEditType.EumValueOverride && item.overridePayload) {
      storedChange = buildEumValueOverrideChange({
        changeRequestId: gpChangeRequest.id,
        entityId: item.entityId,
        entityName,
        currentEumValue: decimalStringToNumber(item.currentValuesSnapshot?.effectiveEumValue),
        proposedEumValue: decimalStringToRequiredNumber(item.overridePayload.eumOverrideValue),
        metadata,
      });
    } else if (item.changeType === BillingEumEditType.EumCalcEdit && item.calcEditPayload) {
      const snapshot = item.currentValuesSnapshot;
      storedChange = buildEumCalcEditChange({
        changeRequestId: gpChangeRequest.id,
        entityId: item.entityId,
        entityName,
        current: {
          eumValue: decimalStringToNumber(snapshot?.effectiveEumValue),
          netCapitalValue: decimalStringToNumber(snapshot?.netCapitalValue),
          totalCommitmentValue: decimalStringToNumber(snapshot?.totalCommitmentValue),
          distributionsValue: decimalStringToNumber(snapshot?.distributions),
        },
        proposed: {
          eumValue: null,
          netCapitalValue: decimalStringToNumber(item.calcEditPayload.netCapitalValue),
          totalCommitmentValue: decimalStringToNumber(
            item.calcEditPayload.totalCommitmentValue,
          ),
          distributionsValue: decimalStringToNumber(item.calcEditPayload.distributions),
        },
        metadata,
      });
    }

    if (!storedChange) {
      continue;
    }

    const existing = entityMap.get(item.entityId) ?? [];
    existing.push(storedChange);
    entityMap.set(item.entityId, existing);
    entityCounts.set(item.entityId, (entityCounts.get(item.entityId) ?? 0) + 1);
  }

  return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
};
